import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Popover from '@radix-ui/react-popover';
import { Bell, CheckCheck, MessageSquare, FileText, UserPlus, X } from 'lucide-react';
import { notificationsApi } from '../api/notifications';
import { formatRelativeTime } from '../lib/utils';
import { toast } from './Toast';

const NOTIFICATION_ICONS: Record<string, typeof Bell> = {
  comment: MessageSquare,
  mention: MessageSquare,
  share: FileText,
  member: UserPlus,
  system: Bell,
};

const NOTIFICATION_ICON_COLORS: Record<string, string> = {
  comment: 'text-accent',
  mention: 'text-accent',
  share: 'text-success',
  member: 'text-info',
  system: 'text-text-tertiary',
};

export function NotificationCenter() {
  const queryClient = useQueryClient();

  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.unreadCount(),
    refetchInterval: 30000,
  });

  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.list({ limit: 20 }),
  });

  const notifications = notificationsData?.items ?? [];

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '操作失败');
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'list'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      toast.success('已全部标记为已读');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '操作失败');
    },
  });

  const unreadCount = unreadData?.count ?? 0;

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="relative flex h-9 w-9 items-center justify-center rounded-md text-text-secondary hover:bg-bg-subtle"
          aria-label={`通知${unreadCount > 0 ? `（${unreadCount} 条未读）` : ''}`}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger px-1 text-[10px] font-medium text-white leading-none">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className="z-50 w-[380px] max-w-[calc(100vw-32px)] rounded-xl bg-bg-surface shadow-xl ring-1 ring-black/5"
          sideOffset={8}
          align="end"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-medium text-[#1a1a2e]">通知</h3>
            {notifications.some((n) => !n.read) && (
              <button
                type="button"
                disabled={markAllReadMutation.isPending}
                onClick={() => markAllReadMutation.mutate()}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover"
              >
                <CheckCheck className="h-3.5 w-3.5" />
                全部已读
              </button>
            )}
          </div>

          <div className="max-h-[400px] overflow-auto">
            {isLoading ? (
              <div className="space-y-2 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-14 animate-shimmer rounded-lg bg-bg-subtle" />
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-center">
                <Bell className="h-8 w-8 text-text-tertiary" />
                <p className="mt-2 text-sm text-text-tertiary">暂无通知</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((notification) => {
                  const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                  const iconColor = NOTIFICATION_ICON_COLORS[notification.type] || 'text-text-tertiary';
                  return (
                    <div
                      key={notification.id}
                      className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                        !notification.read ? 'bg-accent-50/30' : ''
                      } hover:bg-bg-subtle cursor-pointer`}
                      onClick={() => {
                        if (!notification.read) {
                          markReadMutation.mutate(notification.id);
                        }
                        if (notification.link) {
                          window.location.href = notification.link;
                        }
                      }}
                    >
                      <div className={`mt-0.5 ${iconColor}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 overflow-hidden">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium text-text-primary truncate">
                            {notification.title}
                          </span>
                          {!notification.read && (
                            <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
                          )}
                        </div>
                        {notification.body && (
                          <p className="mt-0.5 text-xs text-text-secondary line-clamp-2">
                            {notification.body}
                          </p>
                        )}
                        <span className="mt-1 block text-xs text-text-tertiary">
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Popover.Close className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded text-text-tertiary hover:bg-bg-subtle" aria-label="关闭">
            <X className="h-3 w-3" />
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}